import { Duration } from "../../../components/time-display/Duration.tsx";
import type {
    GroupedTaskBatchesScanResult,
    OverallTaskHistoryResult, ProjectTaskBatchesResult, TaskOverlaps, TaskSourceBreakdown,
} from "../scan-model/GroupedTaskBatchesScanResult.tsx";
import Styles from "./TaskAnalysisDisplay.module.scss";
import {implementationName} from "../../../metl-models/util/ComponentMap.ts";
import {IndividualTaskAnalysisDisplay} from "./IndividualTaskAnalysisDisplay.tsx";
import {Tooltip} from "@matillion/component-library";

function ComponentTime({record, runs}: {record: Record<number, number>, runs: Record<number, number>}) {
    return <table>
        <thead>
            <tr>
                <th>Component</th>
                <th>Total Execution Time</th>
                <th>Average Execution Time</th>
            </tr>
        </thead>
        <tbody>
            {Object.entries(record).map(([component, time]) => (
                <tr key={component}>
                    <td>{implementationName(parseInt(component))}</td>
                    <td><Duration duration={time}/></td>
                    <td><Duration duration={time/runs[parseInt(component)]}/></td>
                </tr>
            ))}
        </tbody>
    </table>
}

function ComponentRowCounts({record, runs}: {record: Record<number, number>, runs: Record<number, number>}) {
    return <table>
        <thead>
            <tr>
                <th>Component</th>
                <th>Total Row Count</th>
                <th>Average Row Count</th>
            </tr>
        </thead>
        <tbody>
            {Object.entries(record).map(([component, rowCount]) => (
                <tr key={component}>
                    <td>{implementationName(parseInt(component))}</td>
                    <td>{rowCount}</td>
                    <td>{rowCount/runs[parseInt(component)]}</td>
                </tr>
            ))}
        </tbody>
    </table>
}

function ComponentRuns({record}: {record: Record<number, number>}) {
    return <table>
        <thead>
            <tr>
                <th>Component</th>
                <th>Runs</th>
            </tr>
        </thead>
        <tbody>
            {Object.entries(record).map(([component, runs]) => (
                <tr key={component}>
                    <td>{implementationName(parseInt(component))}</td>
                    <td>{runs}</td>
                </tr>
            ))}
        </tbody>
    </table>
}

function Overlap({overlap}: {overlap: TaskOverlaps}) {
    return <div>
        <Tooltip content={"The highest number of tasks that were running concurrently in a single batch"}>
            <p>Max concurrent tasks: {overlap.highestOverlap}</p>
        </Tooltip>
        <Tooltip content={"The average number of concurrent tasks that were running across a single batch"}>
            <p>Average running utilisation: {overlap.meanOverlap}</p>
        </Tooltip>
        <Tooltip content={"The average number of concurrent tasks that were running across the scan period"}>
            <p>Average overall utilisation: {overlap.meanOverallOverlap}</p>
        </Tooltip>
    </div>
}

function Sources({sources}: {sources: TaskSourceBreakdown}) {
    return <table>
        <thead>
            <tr>
                <th>Source</th>
                <th>Count</th>
            </tr>
        </thead>
        <tbody>
            {Object.entries(sources).map(([source, count]) => (
                <tr key={source}>
                    <td>{source}</td>
                    <td>{count}</td>
                </tr>
            ))}
        </tbody>
    </table>
}

function GroupedDisplay({grouped}: {grouped: GroupedTaskBatchesScanResult}) {
    return <>
        <p>Total task batches: {grouped.numberOfTaskBatches}</p>
        <Overlap overlap={grouped.overlaps} />
        <div className={Styles.TaskAnalysisRow}>
            <Sources sources={grouped.taskSources} />
            <ComponentRuns record={grouped.componentImplementationRuns} />
            <ComponentTime record={grouped.componentImplementationTimes} runs={grouped.componentImplementationRuns} />
            <ComponentRowCounts record={grouped.componentImplementationRowCounts} runs={grouped.componentImplementationRuns} />
        </div>
    </>
}

function ProjectDisplay({project}: {project: ProjectTaskBatchesResult}) {
    return <div>
        <h4>{project.projectId.groupName}/{project.projectId.projectName}</h4>
        <GroupedDisplay grouped={project} />
    </div>
}

interface Props {
    analysis: OverallTaskHistoryResult
}
export function EnhancedTaskAnalysisDisplay({ analysis }: Props) {
    return (
        <div className={Styles.taskAnalysisDisplay}>
            <div className={Styles.analysisHeader}>
                <h2>Task Analysis Report</h2>
                <p>Comprehensive analysis of METL task execution performance</p>
            </div>

            <div>
                <h3>Overall Task Statistics</h3>
                <GroupedDisplay grouped={analysis.overall} />
            </div>

            <div>
                <h3>Project Breakdown</h3>
                {analysis.projects.map(project => <ProjectDisplay key={project.projectId.groupName + "/" + project.projectId.projectName} project={project} />)}
            </div>
            <div>
                <h3>Individual Tasks</h3>
                <IndividualTaskAnalysisDisplay tasks={analysis.individualResults} />
            </div>
        </div>
    );
}